import { useEffect, useRef } from "react";
import { Star, Quote, ExternalLink } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import content from "@/data/content/testimonials.json";

const REVIEWS = content.reviews;

// Gentle auto-scrolling, manually swipeable review carousel.
export const Reviews = () => {
  const trackRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf;
    const step = () => {
      if (!pausedRef.current) {
        el.scrollLeft += 0.5;
        // loop: content is duplicated, reset at halfway point
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft -= el.scrollWidth / 2;
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const pause = () => (pausedRef.current = true);
  const resume = () => (pausedRef.current = false);

  const items = [...REVIEWS, ...REVIEWS];

  return (
    <section data-testid="reviews-section" className="bg-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal>
            <span className="text-xs tracking-[0.25em] uppercase text-terracotta font-medium">
              {content.eyebrow}
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-3 font-serif text-3xl md:text-5xl font-semibold text-brown">
              {content.heading}
            </h2>
          </Reveal>
        </div>
      </div>

      {/* Full-bleed carousel */}
      <div
        ref={trackRef}
        data-testid="reviews-carousel"
        onMouseEnter={pause}
        onMouseLeave={resume}
        onTouchStart={pause}
        onTouchEnd={resume}
        className="flex gap-4 overflow-x-auto no-scrollbar px-4 sm:px-6 lg:px-8 pb-6"
      >
        {items.map((r, i) => (
          <div
            key={i}
            data-testid={i < REVIEWS.length ? `review-card-${i}` : undefined}
            className="shrink-0 w-[78vw] sm:w-[320px] flex flex-col bg-white rounded-2xl p-5 shadow-[0_8px_30px_rgba(74,49,37,0.06)]"
          >
            <Quote className="h-6 w-6 text-gold/60" />
            <div className="mt-3 flex gap-1">
              {Array.from({ length: r.stars }).map((_, s) => (
                <Star key={s} className="h-3.5 w-3.5 fill-gold text-gold" />
              ))}
            </div>
            <p className="mt-3 text-sm text-brown/80 leading-relaxed flex-1">"{r.text}"</p>
            <div className="mt-4 flex items-center justify-between">
              <div>
                <p className="font-serif text-base text-brown">{r.name}</p>
                <p className="text-[10px] uppercase tracking-wider text-brown/50">{r.platform}</p>
              </div>
              <a
                href={r.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={i < REVIEWS.length ? `review-original-${i}` : undefined}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-terracotta hover:text-terracotta-dark transition-colors"
              >
                Read Original <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Reviews;
